const RETRYABLE_GRAPHQL_ERROR_CODES = new Set(['THROTTLED', 'INTERNAL_SERVER_ERROR', 'TIMEOUT']);

export const PRICE_FETCH_RETRY_DEFAULTS = Object.freeze({
  maxRetries: 2,
  baseDelayMs: 150,
  timeBudgetMs: 1500,
});

const priceFetchFailure = (error, { retryable, status = null, nodes = [] }) => ({
  ok: false,
  nodes,
  retryable,
  status,
  error,
});

/**
 * Resolves to `{ ok, nodes, retryable, status, error }`. `ok` is true only when Shopify
 * returned a complete, error-free response, so callers may treat absent IDs as confirmed missing.
 * When `ok` is false, `nodes` may still hold partial data but absent IDs are unresolved.
 * Never rejects.
 */
export const fetchPriceData = async (variantIds, config) => {
  const ids = Array.isArray(variantIds) ? variantIds : [];
  const query = `
    query getPrice($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          availableForSale
          comingSoon: metafield(namespace: "custom", key: "coming_soon") {
             value
          }
          pageViews: metafield(namespace: "custom", key: "page_views") {
            value
          }
          price { amount currencyCode }
          compareAtPrice { amount currencyCode }
          ${config?.includePromoContext ? `
          isGwp: metafield(namespace: "custom", key: "gwp") { value }
          product { id collections(first: 100) { nodes { id } } }` : ''}
        }
      }
    }
  `;

  if (ids.length === 0) {
    return { ok: true, nodes: [], retryable: false, status: null, error: null };
  }

  const controller = config?.timeoutMs > 0 ? new AbortController() : null;
  const timeoutId = controller ? setTimeout(() => controller.abort(), config.timeoutMs) : null;

  let response;
  try {
    response = await fetch(config.GRAPHQL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables: { ids, country: Shopify.country } }),
      signal: controller?.signal,
    });
  } catch (error) {
    clearTimeout(timeoutId);
    return priceFetchFailure(`Network error: ${error?.message || error}`, { retryable: true });
  }

  let result;
  try {
    result = await response.json();
  } catch (error) {
    // Body unreadable: a 4xx is still a request problem; anything else is treated as transient.
    return priceFetchFailure(`Invalid response body (HTTP ${response.status})`, {
      retryable: !(response.status >= 400 && response.status < 500),
      status: response.status,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  const rawNodes = result?.data?.nodes;
  const nodes = Array.isArray(rawNodes) ? rawNodes.filter((n) => n !== null) : [];

  if (!response.ok) {
    return priceFetchFailure(`HTTP ${response.status}`, {
      retryable: response.status >= 500,
      status: response.status,
      nodes,
    });
  }

  if (Array.isArray(result?.errors) && result.errors.length > 0) {
    const retryable = result.errors.every((e) => RETRYABLE_GRAPHQL_ERROR_CODES.has(e?.extensions?.code));
    return priceFetchFailure(`GraphQL errors: ${result.errors.map((e) => e?.message).join('; ')}`, {
      retryable,
      status: response.status,
      nodes,
    });
  }

  // Shopify returns one entry (null when missing) per requested ID, so a length mismatch means a truncated response.
  if (!Array.isArray(rawNodes) || rawNodes.length !== ids.length) {
    return priceFetchFailure('Malformed or incomplete response', { retryable: true, status: response.status, nodes });
  }

  return { ok: true, nodes, retryable: false, status: response.status, error: null };
};

/**
 * Retries `fetchPriceData` on transient failures (network, 5xx, malformed body) within a small
 * total time budget. 4xx and GraphQL validation errors are returned immediately.
 */
export const fetchPriceDataWithRetry = async (variantIds, config, options = {}) => {
  const { maxRetries, baseDelayMs, timeBudgetMs } = { ...PRICE_FETCH_RETRY_DEFAULTS, ...options };
  const deadline = Date.now() + timeBudgetMs;
  let result = await fetchPriceData(variantIds, config);

  for (let attempt = 1; attempt <= maxRetries && !result.ok && result.retryable; attempt += 1) {
    const delay = baseDelayMs * 2 ** (attempt - 1);
    const remaining = deadline - Date.now() - delay;

    if (remaining <= 0) break;

    await new Promise((resolve) => setTimeout(resolve, delay));
    result = await fetchPriceData(variantIds, { ...config, timeoutMs: remaining });
  }

  return result;
};

export const formatMoney = (price, settings) => {
  if (settings.currencyCodeEnabled === 'true' && settings.moneyWithCurrencyFormat) {
    const cents = Math.round(price * 100);
    const formatString = settings.moneyWithCurrencyFormat;
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      let fixedNumber = (number / 100.0).toFixed(precision);
      let parts = fixedNumber.split('.');
      let dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      let centsPart = parts[1] ? (decimal + parts[1]) : '';
      return dollars + centsPart;
    };

    const match = formatString.match(placeholderRegex);
    if (!match) return price.toFixed(2);

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  } else {
    return new Intl.NumberFormat(`${Shopify.locale}-${Shopify.country}`, {
      style: 'currency',
      currency: Shopify.currency.active,
	  currencyDisplay: 'narrowSymbol'
    }).format(price);
  }
};
